/**
 * 이상한 방 - 밀폐 실내 셸 지오메트리
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3절(좌표)·3.1절(셀 점유)
 * 데이터 원천: `core/floorPlan.ts`
 *
 * 이 파일은 4벽 + 천장 + 내벽으로 **완전히 밀폐된** 여러 구획을 만든다. 밀폐 셸은 1인칭 시절의
 * 결정이다(시야 차단이 게임 그 자체였다). 시점이 아이소메트릭으로 바뀐 뒤의 요구는 결정 4가 설명한다.
 *
 * ## 결정 1 - 모든 면은 단면(FrontSide)이고 노멀이 실내를 향한다
 *
 * 이 한 줄이 "에디터에서는 천장과 카메라 쪽 벽이 가려선 안 된다"는 요구를 **비용 0으로** 해결한다.
 *
 *  - 실내(카메라가 방 안일 때): 어느 방에 서 있든 그 방의 네 벽·천장·바닥이 전부 카메라를 향한다 → 완전 밀폐로 보인다.
 *  - 실외(궤도 에디터): 카메라 쪽 면은 뒷면이라 GPU가 컬링한다 → 저절로 인형의 집(dollhouse) 뷰가 된다.
 *    가까운 방의 앞벽이 사라지고 먼 벽이 배경으로 남으므로, 어느 각도에서 봐도 안이 보인다.
 *
 * 매 프레임 도는 컬링 코드도, 벽 페이드아웃도 필요 없다. 그리고 Three의 레이캐스트가
 * `material.side`를 존중하므로 **화면에서 사라진 면은 클릭에도 걸리지 않는다** - 시각과 입력이 자동으로 일치한다.
 *
 * 단 하나 자동으로 해결되지 않는 것이 천장이다. 천장 노멀은 아래(−y)를 향하므로 위에서 볼 때는 컬링되지만,
 * 카메라가 방보다 낮게 내려가면 천장 밑면이 실내를 통째로 가린다. 그래서 `setCutawayMode`가 존재한다.
 *
 * ## 결정 2 - 벽면은 방마다 자기 안쪽 면만 그린다
 *
 * 벽을 두께 있는 박스로 만들지 않는다. 각 구획이 자기 rect 경계에 **안쪽을 향한 면**을 세우고,
 * 이웃 구획이 반대편에 자기 면을 세운다. 두 면 사이 0.25m가 실제 벽 두께다.
 * 박스를 쓰면 바깥 면이 남아 결정 1의 인형의 집 뷰가 성립하지 않는다.
 *
 * 그 대신 **문틀·창틀이 필수**가 된다. 개구부에서 벽 두께가 보이지 않으면 종잇장처럼 읽히기 때문이다.
 * 걸레받이·문틀·창틀은 장식이 아니라 "상자"와 "방"을 가르는 최소 요소다.
 *
 * ## 결정 3 - 무광, 후처리 없음
 *
 * 벽·바닥의 거칠기를 크게 잡아 반사를 없앤다. 이 게임의 화면은 분위기가 아니라 **판정 근거**이며,
 * 하이라이트가 흐르면 소품 색·위치 변화가 묻힌다. 같은 이유로 해상도를 깎는 후처리(픽셀화 등)는 금지다.
 *
 * ## 결정 4 - 아이소메트릭 전환: 셸을 "섹션"으로 쪼갠다
 *
 * 시점이 1인칭에서 아이소메트릭으로 바뀌면서 셸의 요구가 한 번 더 뒤집혔다.
 * 아이소메트릭은 **고정 부감**이므로 밀폐 실내를 그대로 두면 아무것도 보이지 않는다.
 * 그래서 `render/cutaway.ts`가 카메라 방향에 따라 구조물을 걷어내는데,
 * 걷어내려면 **끄고 켤 수 있는 단위**가 있어야 한다. 그 단위가 섹션(`ShellSectionRole` × `RoomSide`)이다.
 *
 * 섹션을 메시로 쪼개지 않고 **한 메시 안의 지오메트리 그룹 + 머티리얼 배열**로 만든 이유는 드로우콜이다.
 * Three는 `material.visible === false`인 그룹을 렌더 리스트에 넣지 않으므로,
 * 그룹 단위 끄기는 메시를 쪼갠 것과 같은 효과를 내면서 씬 그래프 노드 수는 그대로다
 * (결정 2 주석의 "셸 전체 20여 콜"이 유지된다 - 오히려 절반이 꺼져 있어 더 적게 그린다).
 *
 * 대가가 하나 있다. 레이캐스터는 `material.side`는 존중하지만 **`material.visible`은 보지 않으므로**,
 * 결정 1이 공짜로 주던 "화면에서 사라진 면은 클릭에도 안 걸린다"가 그룹 단위에서는 깨진다.
 * 그래서 `isShellHitVisible`을 같이 내보낸다 - 씬을 레이캐스트하는 쪽이 교차마다 통과시켜야 한다.
 *
 * ## 결정 5 - 내벽은 허리 높이에서 자른다 (컷어웨이의 핵심 선택)
 *
 * 아이소메트릭에서 내벽을 **전부 남기면** 뒤쪽 구획이 통째로 가려지고(원칙 2 위반),
 * **전부 지우면** 9구획이 하나의 큰 바닥으로 뭉개져 어디가 어느 방인지 읽히지 않는다.
 * 그래서 내벽은 `INTERIOR_CUT_HEIGHT_METERS`에서 잘라 **아래만 남긴다**.
 * 구획 경계는 허리 높이 띠로 읽히고, 그 위로는 시야가 통과한다.
 *
 * 잘린 단면에는 **벽마루(`wall-top`)**를 덮는다. 벽면이 두께 0인 단면(결정 2)이라
 * 마루가 없으면 잘린 벽이 종잇장 한 장으로 보인다 - 걸레받이·문틀과 같은 이유의 필수 디테일이다.
 *
 * 허리 아래가 가리는 바닥 띠(약 1.4m)는 **카메라 4방향 회전이 회수한다.**
 * 한 방향에서 스툽 뒤에 숨은 소품은 반대 방향에서 정면으로 드러난다 -
 * 원칙 2를 지키는 주체는 벽 높이가 아니라 회전이며, 그래서 회전은 선택 기능이 아니다.
 *
 * ## 결정 6 - 외벽은 백페이스 컬링에 맡기고, 컷어웨이는 그 나머지를 맡는다
 *
 * 결정 1 덕분에 카메라 쪽 외벽은 GPU가 알아서 컬링한다. 심지어 **타이밍이 완벽하다** -
 * 면이 앞/뒤로 뒤집히는 순간은 시선과 면이 평행한 순간, 즉 화면 투영 면적이 0인 순간이기 때문이다.
 * 회전 중에 벽이 "팍" 사라지는 게 보이지 않는 이유가 이것이다.
 *
 * 다만 컬링이 처리하지 못하는 것이 셋 있다.
 *  - **창틀**은 상자라 어느 각도에서도 면이 남는다 → 벽이 사라진 자리에 흰 액자만 떠 있게 된다
 *  - **걸레받이 윗면**은 +y를 향하므로 부감에서 항상 보인다 → 사라진 벽을 따라 흰 띠가 남는다
 *  - **잘린 내벽**은 애초에 컬링 대상이 아니다(지오메트리를 지워야 한다)
 * 그래서 섹션 메타(`side`)를 심어 두고 컷어웨이가 방향별로 명시적으로 끈다.
 * 명시적으로 끄면 드로우콜도 같이 줄고, 무엇보다 **테스트가 가능해진다** - 공정성이 걸린 가시성이므로 중요하다.
 *
 * ## 렌더 레이어 규약
 *
 * Three 객체의 제자리 변경을 허용한다(`scene.ts` 참조). 게임 상태는 읽지도 쓰지도 않는다.
 */

import * as THREE from 'three'

import {
  DOOR_HEIGHT_CELLS,
  OUTER_WALL_CELLS,
  WALL_THICKNESS_CELLS,
  assertValidFloorPlan,
  createSingleRoomPlan,
  openingRect,
  openingSide,
  requireRoom,
  walkableFootprintCells,
  footprintKey,
  type Axis,
  type Cell2,
  type FloorPlan,
  type Room,
  type RoomSide,
  type RoomTag,
} from '../core/floorPlan'
import { CELL_SIZE_METERS, type RoomBounds } from '../core/types'
import { roomWorldSize, type SceneCtx } from './scene'

/* --- 팔레트 --------------------------------------------------------------------- */

/**
 * 구획 성격별 바닥·벽 색. 목표 룩은 카툰 로우폴리 + 따뜻한 파스텔 실내다.
 *
 * 값을 고른 기준은 예쁨이 아니라 **가독성**이다.
 *  - 벽은 전부 휘도 0.75 이상의 밝은 면으로 통일했다. 소품 팔레트(`catalog/palette.ts`)의
 *    최상단 색(우유빛 Y=0.88)을 빼면 모든 소품이 벽보다 어두워 실루엣이 항상 떠오른다.
 *  - 바닥은 벽보다 한 단계 어둡다. 소품 대부분이 바닥에 서므로 여기서 대비가 가장 많이 필요하다.
 *  - 구획끼리는 색상(hue)으로 갈랐다. 욕실은 찬 타일톤, 침실은 따뜻한 살구톤 -
 *    1인칭에서 "지금 어느 방인가"가 벽 색만으로 읽혀야 20m 공간에서 길을 잃지 않는다.
 */
type ShellPalette = Readonly<{ floor: number; wall: number }>

const ROOM_PALETTE: Readonly<Record<RoomTag, ShellPalette>> = {
  living: { floor: 0xc7a67e, wall: 0xf1e7d7 },
  kitchen: { floor: 0xd9d0c1, wall: 0xe5efe7 },
  bedroom: { floor: 0xc0a086, wall: 0xf3e4df },
  bath: { floor: 0xcedde3, wall: 0xdeecf1 },
  hall: { floor: 0xc5b7a3, wall: 0xece7dd },
}

/** 천장. 벽보다 살짝 밝아야 실내가 답답해지지 않는다. */
const CEILING_COLOR = 0xf5f2eb

/** 걸레받이·문틀·창틀 공통. 흰 목재 트림 톤 - 구획 색이 뭐든 경계선처럼 읽힌다. */
const TRIM_COLOR = 0xfbf8f2

/**
 * 벽이 깔고 앉은 바닥. 실내에서는 절대 보이지 않고, 궤도 뷰에서 위에서 내려다볼 때만 보인다.
 * 어둡게 둬서 평면도의 벽 선처럼 읽히게 한다.
 */
const WALL_BASE_COLOR = 0x8b8177

/** 창밖. 스카이박스 없이 밝은 색면 하나로 처리한다 - 창은 광원이자 "바깥이 있다"는 신호일 뿐이다. */
const OUTSIDE_COLOR = 0xe2f0f8

/** 유리. 반투명이되 뒤 색을 죽이지 않을 만큼만 불투명하다. */
const GLASS_COLOR = 0xc4e2ee
const GLASS_OPACITY = 0.26

/* --- 치수 ----------------------------------------------------------------------- */

const DOOR_HEIGHT_METERS = DOOR_HEIGHT_CELLS * CELL_SIZE_METERS

/**
 * 컷어웨이에서 내벽을 자르는 높이(셀). 4셀 = 1.0m - 성인 허리 높이이자 주방 카운터 높이다.
 *
 * 이 값 하나가 "구획이 읽히는가" ↔ "뒤쪽이 보이는가"의 저울이다.
 *  - 올리면 구획 경계가 뚜렷해지는 대신 벽 뒤 바닥 띠가 넓어진다(부감 35°에서 높이 × 1.43m)
 *  - 내리면 다 보이는 대신 9구획이 하나의 큰 바닥으로 뭉개진다
 * 1.0m는 "벽이다"라고 읽히는 최소 높이에 가깝다. 플레이테스트로 조정할 항목이므로 상수 하나로 묶어 둔다.
 */
const INTERIOR_CUT_HEIGHT_CELLS = 4

/** 내벽 절단 높이(미터). 벽마루(`wall-top`)도 이 높이에 덮인다. */
export const INTERIOR_CUT_HEIGHT_METERS = INTERIOR_CUT_HEIGHT_CELLS * CELL_SIZE_METERS

/** 걸레받이 규격. 그리드 배수가 아니어도 된다 - 판정에 관여하지 않는 순수 장식이기 때문이다. */
const BASEBOARD_HEIGHT_METERS = 0.09
const BASEBOARD_DEPTH_METERS = 0.02

/** 문틀·창틀 두께. 개구부 안쪽으로 들어가므로 문 실폭이 좌우 이만큼씩 줄어든다. */
const FRAME_THICKNESS_METERS = 0.05

/** 유리가 벽 두께의 어디에 놓이는가(0 = 실외면, 1 = 실내면). 가운데가 창틀 깊이감이 가장 좋다. */
const GLASS_DEPTH_RATIO = 0.5

/** 창밖 색면을 개구부보다 이만큼 넓게 잡는다. 비스듬히 봐도 색면 가장자리가 보이지 않게. */
const OUTSIDE_PANEL_MARGIN_METERS = 0.2

/** 무광. 값이 낮으면 하이라이트가 흘러 소품 변화가 묻힌다(결정 3). */
const SURFACE_ROUGHNESS = 0.95
const TRIM_ROUGHNESS = 0.75

/** 유리는 무광이면 창처럼 보이지 않는다. 셸에서 유일하게 거칠기를 낮추는 면이다. */
const GLASS_ROUGHNESS = 0.05

/* --- 씬 트리 이름 --------------------------------------------------------------- */

export const ROOM_SHELL_GROUP_NAME = 'room-shell'
export const SHELL_CEILING_NAME = 'shell-ceiling'

/**
 * 셸 표면 식별자. 각 메시의 `name`과 `userData.shellSurface`에 같은 값이 들어간다.
 * `tapPicker.ts`·`picker.ts`가 표면 히트에서 "바닥을 맞혔는가"를 구별하는 데 쓰라고 붙여 둔 것이며,
 * 메시 참조를 주고받는 것보다 결합이 얕다.
 */
export type ShellSurface = 'floor' | 'wall' | 'ceiling' | 'trim' | 'glass' | 'outside'

export const SHELL_SURFACE_KEY = 'shellSurface'
/** 구획 소속 메시에 붙는 `userData` 키. 조명 리그·디버그 UI가 방별로 메시를 찾을 때 쓴다. */
export const SHELL_ROOM_KEY = 'shellRoomId'

/* --- 컷어웨이 섹션 계약 ---------------------------------------------------------- */

/**
 * 컷어웨이가 껐다 켜는 최소 단위의 역할.
 *
 * `wall`/`frame`은 **외벽**용(전체 높이 유지), `*-lower`/`*-upper`는 **내벽**용(허리에서 절단),
 * `wall-top`은 잘린 단면을 덮는 마루다. 근거는 파일 상단 결정 5·6.
 */
export type ShellSectionRole =
  | 'wall'
  | 'wall-lower'
  | 'wall-upper'
  | 'wall-top'
  | 'baseboard'
  | 'frame'
  | 'frame-lower'
  | 'frame-upper'

/**
 * 섹션 메타 한 줄. 메시 하나의 `userData[SHELL_SECTIONS_KEY]`에 배열로 들어간다.
 *
 * `materialIndex`는 그 메시의 머티리얼 배열 인덱스이자 지오메트리 그룹 인덱스다 -
 * 컷어웨이는 이 인덱스로 머티리얼을 집어 `visible`·`opacity`만 건드린다.
 */
export type ShellSectionMeta = Readonly<{
  materialIndex: number
  role: ShellSectionRole
  /**
   * 이 섹션이 서 있는 벽면. `null`이면 **방향과 무관**하다 -
   * 문틀·벽마루는 두 구획이 공유하는 물건이라 어느 쪽에서 봐도 남아야 한다.
   */
  side: RoomSide | null
}>

/** 섹션 메타 배열이 붙는 `userData` 키. `render/cutaway.ts`와의 계약이다. */
export const SHELL_SECTIONS_KEY = 'shellSections'

/** 셸 그룹에 심는 평면도 id. 셸과 다른 평면도로 컷어웨이를 만들면 즉시 터지게 하려고 둔다. */
export const SHELL_PLAN_ID_KEY = 'shellPlanId'

/** 컷어웨이 모드에서**만** 보이는 역할. 1인칭에서는 벽 속에 묻히므로 그릴 이유가 없다. */
const CUTAWAY_ONLY_ROLES: readonly ShellSectionRole[] = ['wall-top']

/** 컷어웨이 모드에서 잘려 나가는 역할. 내벽 허리 위와 그 위에 걸린 문틀 상단이다. */
const CUT_OFF_ROLES: readonly ShellSectionRole[] = ['wall-upper', 'frame-upper']

/**
 * 모드(1인칭 밀폐 ↔ 아이소메트릭 컷어웨이)만으로 가시성이 정해지는 역할인가.
 *
 * 이 경계가 소유권 분리선이다. `setCutawayMode`는 **모드 전용 역할만** 건드리고,
 * 방향별 숨김(`cutaway.ts`)은 나머지를 건드린다. 둘이 같은 머티리얼을 쓰면
 * 모드 전환 한 번에 방향 상태가 조용히 날아간다.
 */
export function isModeOwnedRole(role: ShellSectionRole): boolean {
  return CUTAWAY_ONLY_ROLES.includes(role) || CUT_OFF_ROLES.includes(role)
}

/** 모드만 봤을 때 이 역할이 보여야 하는가. 방향은 여기서 판단하지 않는다. */
export function isSectionVisibleForMode(role: ShellSectionRole, cutaway: boolean): boolean {
  if (CUTAWAY_ONLY_ROLES.includes(role)) return cutaway
  if (CUT_OFF_ROLES.includes(role)) return !cutaway
  return true
}

/** 셸 핸들. `plan`을 같이 들고 있어 충돌·배치 생성기가 렌더와 같은 평면도를 보게 된다. */
export type RoomShell = Readonly<{
  group: THREE.Group
  plan: FloorPlan
  ceiling: THREE.Mesh
}>

export type RoomShellOptions = Readonly<{
  /** 천장을 걷어낸 궤도(에디터) 뷰로 시작할지. 기본값 `true` - 만들 때는 위에서 본다. */
  cutaway?: boolean
}>

/* --- 지오메트리 버퍼 ------------------------------------------------------------ */

type Vec3 = readonly [number, number, number]

/**
 * 사각형 면을 모아 하나의 `BufferGeometry`로 굽는 버퍼.
 *
 * `PlaneGeometry` 메시를 면마다 만들지 않는 이유는 드로우콜이다.
 * 9개 구획의 벽면은 개구부 때문에 수십 조각으로 쪼개지는데, 조각마다 메시를 만들면
 * 셸 하나가 200콜을 넘긴다. 여기에 소품 300~500개가 더해지면 모바일에서 먼저 무너진다.
 * 면을 미리 합쳐 두면 셸 전체가 20여 콜로 끝난다.
 */
type SurfaceBuffer = { positions: number[]; normals: number[]; indices: number[] }

function createBuffer(): SurfaceBuffer {
  return { positions: [], normals: [], indices: [] }
}

/**
 * 사각형 하나. 정점은 반시계 방향이어야 하며 노멀은 `cross(b−a, d−a)`로 계산된다.
 * 노멀을 인자로 받지 않는 이유: 두 벌이 되면 winding과 노멀이 어긋나 뒷면이 보이는 버그가 조용히 생긴다.
 */
function addQuad(buffer: SurfaceBuffer, a: Vec3, b: Vec3, c: Vec3, d: Vec3): void {
  const edge1: Vec3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
  const edge2: Vec3 = [d[0] - a[0], d[1] - a[1], d[2] - a[2]]

  const nx = edge1[1] * edge2[2] - edge1[2] * edge2[1]
  const ny = edge1[2] * edge2[0] - edge1[0] * edge2[2]
  const nz = edge1[0] * edge2[1] - edge1[1] * edge2[0]
  const length = Math.hypot(nx, ny, nz)
  if (length === 0) return // 퇴화 사각형(폭 0). 조용히 버려도 잃는 것이 없다.

  const base = buffer.positions.length / 3
  for (const vertex of [a, b, c, d]) {
    buffer.positions.push(vertex[0], vertex[1], vertex[2])
    buffer.normals.push(nx / length, ny / length, nz / length)
  }
  buffer.indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
}

/** 축 정렬 박스. 6면 전부 바깥을 향한다. 걸레받이 상판·문틀·창틀에 쓴다. */
function addBox(buffer: SurfaceBuffer, min: Vec3, max: Vec3): void {
  const [x0, y0, z0] = min
  const [x1, y1, z1] = max

  addQuad(buffer, [x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]) // +x
  addQuad(buffer, [x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]) // −x
  addQuad(buffer, [x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0]) // +y
  addQuad(buffer, [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]) // −y
  addQuad(buffer, [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]) // +z
  addQuad(buffer, [x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]) // −z
}

/** 위를 향한 수평면(바닥). */
function addFloorQuad(
  buffer: SurfaceBuffer,
  y: number,
  x0: number,
  x1: number,
  z0: number,
  z1: number,
): void {
  addQuad(buffer, [x0, y, z1], [x1, y, z1], [x1, y, z0], [x0, y, z0])
}

/** 아래를 향한 수평면(천장). */
function addCeilingQuad(
  buffer: SurfaceBuffer,
  y: number,
  x0: number,
  x1: number,
  z0: number,
  z1: number,
): void {
  addQuad(buffer, [x0, y, z0], [x1, y, z0], [x1, y, z1], [x0, y, z1])
}

/**
 * 수직면 한 장. `constAxis`가 면의 법선 축, `sign`이 그 축의 어느 쪽을 향하는지다.
 * `u`는 면을 따라 뻗는 가로축(법선이 z면 x, 법선이 x면 z), `v`는 높이다.
 */
function addVerticalQuad(
  buffer: SurfaceBuffer,
  constAxis: Axis,
  constValue: number,
  u0: number,
  u1: number,
  v0: number,
  v1: number,
  sign: 1 | -1,
): void {
  if (constAxis === 'z') {
    if (sign === 1) {
      addQuad(buffer, [u0, v0, constValue], [u1, v0, constValue], [u1, v1, constValue], [u0, v1, constValue])
    } else {
      addQuad(buffer, [u1, v0, constValue], [u0, v0, constValue], [u0, v1, constValue], [u1, v1, constValue])
    }
    return
  }

  if (sign === 1) {
    addQuad(buffer, [constValue, v0, u1], [constValue, v0, u0], [constValue, v1, u0], [constValue, v1, u1])
  } else {
    addQuad(buffer, [constValue, v0, u0], [constValue, v0, u1], [constValue, v1, u1], [constValue, v1, u0])
  }
}

/** 버퍼를 지오메트리로 굽는다. 면이 하나도 없으면 `null` - 빈 메시를 씬에 남기지 않기 위해서다. */
function toGeometry(buffer: SurfaceBuffer): THREE.BufferGeometry | null {
  if (buffer.positions.length === 0) return null

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(buffer.positions, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(buffer.normals, 3))
  geometry.setIndex(buffer.indices)
  geometry.computeBoundingSphere()
  geometry.computeBoundingBox()

  return geometry
}

/* --- 머티리얼 ------------------------------------------------------------------- */

function createSurfaceMaterial(color: number, roughness: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness: 0,
    // 결정 1. 기본값이지만 셸의 동작 전체가 여기에 걸려 있으므로 명시한다.
    side: THREE.FrontSide,
  })
}

/**
 * 메시 하나를 만들어 그룹에 붙인다. 지오메트리가 비면 아무것도 하지 않는다.
 * `userData`에 표면 종류와 구획 id를 남기는 것이 이 함수의 또 다른 목적이다 -
 * 다른 레이어가 셸 메시를 이름이 아니라 의미로 찾을 수 있어야 결합이 얕게 유지된다.
 */
function attachMesh(
  parent: THREE.Group,
  buffer: SurfaceBuffer,
  material: THREE.Material,
  surface: ShellSurface,
  roomId?: string,
): THREE.Mesh | null {
  const geometry = toGeometry(buffer)
  if (geometry === null) {
    material.dispose()
    return null
  }

  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = roomId === undefined ? `shell-${surface}` : `shell-${surface}-${roomId}`
  mesh.userData[SHELL_SURFACE_KEY] = surface
  if (roomId !== undefined) mesh.userData[SHELL_ROOM_KEY] = roomId
  parent.add(mesh)

  return mesh
}

/* --- 섹션 버퍼 ------------------------------------------------------------------- */

/**
 * (역할 × 벽면)별로 갈라 담는 버퍼 모음. 굽는 시점에 하나의 지오메트리 + 그룹으로 합쳐진다.
 * 메시를 쪼개지 않는 이유는 결정 4(드로우콜)이며, 그래서 셸 메시 개수는 이전과 같다.
 */
type SectionDraft = { readonly role: ShellSectionRole; readonly side: RoomSide | null; readonly buffer: SurfaceBuffer }
type SectionSet = Map<string, SectionDraft>

function createSectionSet(): SectionSet {
  return new Map()
}

/** 해당 섹션의 버퍼. 없으면 만들어 끼운다 - 호출부가 존재 여부를 신경 쓰지 않게 한다. */
function sectionOf(set: SectionSet, role: ShellSectionRole, side: RoomSide | null): SurfaceBuffer {
  const key = `${role}|${side ?? '-'}`
  const found = set.get(key)
  if (found !== undefined) return found.buffer

  const draft: SectionDraft = { role, side, buffer: createBuffer() }
  set.set(key, draft)

  return draft.buffer
}

/** 버퍼 하나를 다른 버퍼 뒤에 이어 붙이고, 붙은 인덱스 구간을 돌려준다. */
function appendBuffer(target: SurfaceBuffer, source: SurfaceBuffer): Readonly<{ start: number; count: number }> {
  const vertexOffset = target.positions.length / 3
  const start = target.indices.length

  // 전개 연산자(`push(...arr)`)를 쓰지 않는다 - 벽 하나가 수천 개 원소라 인자 개수 상한에 걸린다.
  for (const value of source.positions) target.positions.push(value)
  for (const value of source.normals) target.normals.push(value)
  for (const value of source.indices) target.indices.push(value + vertexOffset)

  return { start, count: target.indices.length - start }
}

/**
 * 섹션 모음을 **메시 하나**로 굽는다. 섹션마다 지오메트리 그룹 + 전용 머티리얼이 하나씩 붙고,
 * 그 대응표가 `userData[SHELL_SECTIONS_KEY]`로 남는다.
 *
 * 머티리얼을 섹션마다 새로 만드는 것이 핵심이다 - 공유하면 한 면을 끄는 순간 같은 색의 다른 면도 같이 꺼진다.
 */
function attachSectionedMesh(
  parent: THREE.Group,
  set: SectionSet,
  createMaterial: () => THREE.Material,
  surface: ShellSurface,
  roomId?: string,
): THREE.Mesh | null {
  const drafts = [...set.values()].filter((draft) => draft.buffer.positions.length > 0)
  if (drafts.length === 0) return null

  const merged = createBuffer()
  const ranges: Readonly<{ start: number; count: number }>[] = []
  const materials: THREE.Material[] = []
  const metas: ShellSectionMeta[] = []

  for (const draft of drafts) {
    ranges.push(appendBuffer(merged, draft.buffer))
    metas.push({ materialIndex: materials.length, role: draft.role, side: draft.side })
    materials.push(createMaterial())
  }

  const geometry = toGeometry(merged)
  if (geometry === null) {
    for (const material of materials) material.dispose()
    return null
  }

  for (let index = 0; index < ranges.length; index += 1) {
    const range = ranges[index]
    if (range === undefined) continue
    geometry.addGroup(range.start, range.count, index)
  }

  const mesh = new THREE.Mesh(geometry, materials)
  mesh.name = roomId === undefined ? `shell-${surface}` : `shell-${surface}-${roomId}`
  mesh.userData[SHELL_SURFACE_KEY] = surface
  mesh.userData[SHELL_SECTIONS_KEY] = metas
  if (roomId !== undefined) mesh.userData[SHELL_ROOM_KEY] = roomId
  parent.add(mesh)

  return mesh
}

/**
 * 셸의 모든 섹션을 훑는다. `cutaway.ts`가 메시 구조를 몰라도 되게 하는 유일한 통로다.
 *
 * 메타에 적힌 머티리얼이 실제로 없으면 **던진다.** 조용히 건너뛰면 그 면만 영원히 안 꺼지는데,
 * 화면에서는 "왜 이 벽만 안 사라지지"로 보여 원인 추적이 사실상 불가능해진다.
 */
export function forEachShellSection(
  root: THREE.Object3D,
  visit: (material: THREE.Material, meta: ShellSectionMeta, mesh: THREE.Mesh) => void,
): void {
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return

    const metas = node.userData[SHELL_SECTIONS_KEY]
    if (!Array.isArray(metas)) return

    const materials = Array.isArray(node.material) ? node.material : [node.material]

    for (const meta of metas as readonly ShellSectionMeta[]) {
      const material = materials[meta.materialIndex]
      if (material === undefined) {
        throw new RangeError(
          `셸 섹션 메타가 메시 '${node.name}'의 머티리얼 ${meta.materialIndex}번을 가리키는데 그 자리가 비어 있다`,
        )
      }
      visit(material, meta, node)
    }
  })
}

/**
 * 이 레이캐스트 교차가 **화면에 실제로 보이는 면**에 맞았는가.
 *
 * 결정 4(그룹 단위 끄기)가 깨뜨린 불변식을 되돌리는 함수다.
 * `material.side`는 레이캐스터가 존중하지만 `material.visible`은 **보지 않으므로**,
 * 컷어웨이가 끈 벽이 화면에서 사라진 뒤에도 총알과 탭을 계속 막는다.
 * 잘린 내벽 허리 위가 정확히 그 경우이고, 그건 "안 보이는 벽에 총알이 막히는" 억울한 실패가 된다.
 *
 * `input/tapPicker.ts`·`input/picker.ts`처럼 씬 전체를 레이캐스트하는 쪽은 교차마다 이 함수를 통과시켜야 한다.
 * 셸이 아닌 객체에는 관여하지 않고 `true`를 돌려준다 - 소품의 가시성 판정은 그쪽 규칙이 따로 있다.
 */
export function isShellHitVisible(intersection: THREE.Intersection): boolean {
  const object = intersection.object
  if (!(object instanceof THREE.Mesh)) return true
  if (object.userData[SHELL_SECTIONS_KEY] === undefined) return true
  if (!object.visible) return false

  if (!Array.isArray(object.material)) return object.material.visible

  const materialIndex = intersection.face?.materialIndex
  if (materialIndex === undefined) return true

  const material = object.material[materialIndex]

  return material !== undefined && material.visible
}

/* --- 벽면 조각내기 -------------------------------------------------------------- */

/** 벽면에 뚫리는 구멍(면 로컬 좌표, 미터). `u`는 면을 따라, `v`는 높이. */
type FaceHole = Readonly<{ u0: number; u1: number; v0: number; v1: number }>

/** 구멍을 뺀 실벽 조각. */
type FaceRect = Readonly<{ u0: number; u1: number; v0: number; v1: number }>

const ROOM_SIDES: readonly RoomSide[] = ['south', 'north', 'west', 'east']

/** 면의 법선 축. 'south'/'north' 면은 z를 상수로 갖고 x를 따라 뻗는다. */
const SIDE_CONST_AXIS: Readonly<Record<RoomSide, Axis>> = {
  south: 'z',
  north: 'z',
  west: 'x',
  east: 'x',
}

/** 실내를 향하는 방향. 'south' 면은 방이 +z 쪽에 있으므로 노멀이 +z다. */
const SIDE_INWARD: Readonly<Record<RoomSide, 1 | -1>> = {
  south: 1,
  north: -1,
  west: 1,
  east: -1,
}

function metersOf(cells: number): number {
  return cells * CELL_SIZE_METERS
}

/** 면의 상수 좌표(미터). 방 rect의 해당 경계면이다. */
function sideConstValue(room: Room, side: RoomSide): number {
  const rect = room.rect
  if (side === 'south') return metersOf(rect.z)
  if (side === 'north') return metersOf(rect.z + rect.sizeZ)
  if (side === 'west') return metersOf(rect.x)
  return metersOf(rect.x + rect.sizeX)
}

/** 면이 뻗는 구간 [u0, u1](미터). */
function sideSpan(room: Room, side: RoomSide): Readonly<{ u0: number; u1: number }> {
  const rect = room.rect
  if (SIDE_CONST_AXIS[side] === 'z') {
    return { u0: metersOf(rect.x), u1: metersOf(rect.x + rect.sizeX) }
  }
  return { u0: metersOf(rect.z), u1: metersOf(rect.z + rect.sizeZ) }
}

/**
 * 이 면이 **외벽**인가 - 즉 면 바깥쪽이 건물 외벽 링인가.
 *
 * 이 판정 하나가 "전체 높이로 남길 벽"과 "허리에서 자를 벽"을 가른다.
 * 외벽을 자르면 건물 밖 배경이 보이고, 내벽을 안 자르면 뒤쪽 구획이 사라진다.
 * `assertValidFloorPlan`이 모든 방 rect를 외벽 링 안쪽으로 강제하므로 경계 비교만으로 답이 나온다.
 */
function isExteriorSide(plan: FloorPlan, room: Room, side: RoomSide): boolean {
  const rect = room.rect

  if (side === 'south') return rect.z <= OUTER_WALL_CELLS
  if (side === 'north') return rect.z + rect.sizeZ >= plan.bounds.sizeZ - OUTER_WALL_CELLS
  if (side === 'west') return rect.x <= OUTER_WALL_CELLS

  return rect.x + rect.sizeX >= plan.bounds.sizeX - OUTER_WALL_CELLS
}

function openingHole(
  axis: Axis,
  cell: Cell2,
  width: number,
  startCell: number,
  endCell: number,
): FaceHole {
  const start = axis === 'x' ? cell.x : cell.z
  return {
    u0: metersOf(start),
    u1: metersOf(start + width),
    v0: metersOf(startCell),
    v1: metersOf(endCell),
  }
}

/**
 * 개구부가 붙을 면을 확정한다. 못 찾으면 던진다.
 *
 * `assertValidFloorPlan`을 이미 통과했으므로 여기서 null은 데이터 오류가 아니라
 * 검증기와 렌더의 판정이 갈렸다는 뜻이다. 조용히 건너뛰면 그 개구부가 화면에서만 사라져
 * "문이 있는데 벽으로 막힌" 방이 되고, 통행이 끊긴다.
 */
function requireOpeningSide(room: Room, axis: Axis, cell: Cell2, label: string): RoomSide {
  const side = openingSide(room, axis, cell)
  if (side === null) {
    throw new RangeError(
      `${label} 개구부 (${cell.x}, ${cell.z})가 구획 '${room.id}'의 벽면 위에 있지 않다`,
    )
  }
  return side
}

/**
 * 이 방의 각 면에 뚫릴 구멍을 모은다.
 * 면 배정을 `openingSide`(평면도 모듈)에 위임하는 것이 중요하다 -
 * 렌더가 자체 판정을 두면 "데이터상 문인데 화면에는 벽"이 생긴다.
 */
function collectFaceHoles(plan: FloorPlan, room: Room): Record<RoomSide, FaceHole[]> {
  const holes: Record<RoomSide, FaceHole[]> = { south: [], north: [], west: [], east: [] }

  for (const doorway of plan.doorways) {
    if (doorway.fromRoomId !== room.id && doorway.toRoomId !== room.id) continue
    const side = requireOpeningSide(room, doorway.axis, doorway.cell, '문')
    holes[side].push(openingHole(doorway.axis, doorway.cell, doorway.width, 0, DOOR_HEIGHT_CELLS))
  }

  for (const windowOpening of plan.windows) {
    if (windowOpening.roomId !== room.id) continue
    const side = requireOpeningSide(room, windowOpening.axis, windowOpening.cell, `창 '${windowOpening.id}'`)
    holes[side].push(
      openingHole(
        windowOpening.axis,
        windowOpening.cell,
        windowOpening.width,
        windowOpening.sillCell,
        windowOpening.sillCell + windowOpening.heightCells,
      ),
    )
  }

  for (const side of ROOM_SIDES) holes[side].sort((a, b) => a.u0 - b.u0)

  return holes
}

/**
 * 구멍을 뺀 실벽 조각 목록. u축을 따라 훑으며 구멍마다 위·아래 조각을 남긴다.
 *
 * 한 면 위에서 구멍끼리 u 구간이 겹치면 던진다. 겹친 개구부는 평면도 데이터 오류이고,
 * 조용히 통과시키면 벽에 예상 못 한 큰 구멍이 뚫려 **밀폐가 깨진 채로** 화면에 나온다 -
 * 1인칭에서 그건 배경이 보이는 버그이자 은닉 경로다.
 */
function subtractHoles(
  span: Readonly<{ u0: number; u1: number }>,
  height: number,
  holes: readonly FaceHole[],
): readonly FaceRect[] {
  const solids: FaceRect[] = []
  let cursor = span.u0

  for (const hole of holes) {
    if (hole.u0 < cursor) {
      throw new RangeError(
        `같은 벽면에서 개구부가 겹친다: u=${hole.u0.toFixed(3)} (직전 개구부 끝 ${cursor.toFixed(3)})`,
      )
    }

    if (hole.u0 > cursor) solids.push({ u0: cursor, u1: hole.u0, v0: 0, v1: height })
    if (hole.v0 > 0) solids.push({ u0: hole.u0, u1: hole.u1, v0: 0, v1: hole.v0 })
    if (hole.v1 < height) solids.push({ u0: hole.u0, u1: hole.u1, v0: hole.v1, v1: height })

    cursor = hole.u1
  }

  if (cursor < span.u1) solids.push({ u0: cursor, u1: span.u1, v0: 0, v1: height })

  return solids
}

/* --- 걸레받이 ------------------------------------------------------------------- */

/**
 * 걸레받이 한 구간. 방 안쪽으로 튀어나온 L 프로파일(정면 + 윗면) 두 장만 그린다.
 *
 * 박스를 쓰지 않는 이유는 결정 1이다. 박스의 바깥면은 벽 평면과 같은 위치에서 실외를 향하므로,
 * 궤도 뷰에서 벽이 컬링된 뒤에도 걸레받이만 띠처럼 남아 인형의 집 뷰를 더럽힌다.
 */
function addBaseboardRun(
  buffer: SurfaceBuffer,
  side: RoomSide,
  constValue: number,
  u0: number,
  u1: number,
): void {
  const axis = SIDE_CONST_AXIS[side]
  const inward = SIDE_INWARD[side]
  const face = constValue + inward * BASEBOARD_DEPTH_METERS
  const top = BASEBOARD_HEIGHT_METERS

  addVerticalQuad(buffer, axis, face, u0, u1, 0, top, inward)

  const near = Math.min(constValue, face)
  const far = Math.max(constValue, face)
  if (axis === 'z') addFloorQuad(buffer, top, u0, u1, near, far)
  else addFloorQuad(buffer, top, near, far, u0, u1)
}

/* --- 개구부 부속(문틀·창틀·유리·창밖) -------------------------------------------- */

/** 개구부가 관통하는 벽 띠의 두 경계(미터). 벽이 1셀이므로 폭은 항상 0.25m다. */
function bandRange(axis: Axis, cell: Cell2): Readonly<{ near: number; far: number }> {
  const start = axis === 'x' ? cell.z : cell.x
  return { near: metersOf(start), far: metersOf(start + WALL_THICKNESS_CELLS) }
}

/**
 * 개구부를 두르는 틀 상자 네 개(또는 문은 세 개)를 버퍼에 넣는다.
 * 상자는 벽 두께를 통째로 관통하므로 개구부 안쪽 면(리빌)이 전부 덮인다 -
 * 이것이 없으면 벽이 종잇장으로 보인다(결정 2).
 */
function addOpeningFrame(
  buffer: SurfaceBuffer,
  axis: Axis,
  cell: Cell2,
  width: number,
  v0: number,
  v1: number,
  withSill: boolean,
  clipLow = Number.NEGATIVE_INFINITY,
  clipHigh = Number.POSITIVE_INFINITY,
): void {
  const band = bandRange(axis, cell)
  const start = axis === 'x' ? cell.x : cell.z
  const u0 = metersOf(start)
  const u1 = metersOf(start + width)
  const thickness = FRAME_THICKNESS_METERS

  const boxes: readonly Readonly<{ a0: number; a1: number; b0: number; b1: number }>[] = [
    { a0: u0, a1: u0 + thickness, b0: v0, b1: v1 },
    { a0: u1 - thickness, a1: u1, b0: v0, b1: v1 },
    { a0: u0, a1: u1, b0: v1 - thickness, b1: v1 },
    ...(withSill ? [{ a0: u0, a1: u1, b0: v0, b1: v0 + thickness }] : []),
  ]

  for (const box of boxes) {
    // 높이 구간을 잘라 낸다. 내벽 문틀은 벽과 같은 높이에서 끊겨야 하며,
    // 안 자르면 허리 높이 벽 위에 문틀 상인방만 공중에 떠 있게 된다.
    const b0 = Math.max(box.b0, clipLow)
    const b1 = Math.min(box.b1, clipHigh)
    if (b1 <= b0) continue

    if (axis === 'x') addBox(buffer, [box.a0, b0, band.near], [box.a1, b1, band.far])
    else addBox(buffer, [band.near, b0, box.a0], [band.far, b1, box.a1])
  }
}

/**
 * 내벽 조각 한 장을 절단 높이에서 아래·위로 갈라 담는다.
 * 두 조각의 합은 자르기 전과 정확히 같으므로, 1인칭(컷어웨이 off)에서 둘 다 켜면 원래 벽이 돌아온다.
 */
function addSplitWallQuad(
  set: SectionSet,
  side: RoomSide,
  constValue: number,
  solid: FaceRect,
  inward: 1 | -1,
): void {
  const axis = SIDE_CONST_AXIS[side]
  const cut = INTERIOR_CUT_HEIGHT_METERS

  const lowerTop = Math.min(solid.v1, cut)
  if (lowerTop > solid.v0) {
    const buffer = sectionOf(set, 'wall-lower', side)
    addVerticalQuad(buffer, axis, constValue, solid.u0, solid.u1, solid.v0, lowerTop, inward)
  }

  const upperBottom = Math.max(solid.v0, cut)
  if (solid.v1 > upperBottom) {
    const buffer = sectionOf(set, 'wall-upper', side)
    addVerticalQuad(buffer, axis, constValue, solid.u0, solid.u1, upperBottom, solid.v1, inward)
  }
}

/* --- 조립 ----------------------------------------------------------------------- */

type ShellBuffers = Readonly<{
  wallBase: SurfaceBuffer
  doorFloor: SurfaceBuffer
  /** 걸레받이·문틀·창틀·벽마루가 전부 여기 모인다. 색이 하나라 구획별로 나눌 이유가 없다. */
  trim: SectionSet
  glass: SurfaceBuffer
  outside: SurfaceBuffer
}>

/** 구획 하나의 바닥·벽·걸레받이를 굽는다. 벽면 조각내기의 유일한 호출 지점이다. */
function buildRoom(
  group: THREE.Group,
  plan: FloorPlan,
  room: Room,
  height: number,
  trim: SectionSet,
): void {
  const palette = ROOM_PALETTE[room.tag]
  const rect = room.rect

  const floor = createBuffer()
  addFloorQuad(
    floor,
    0,
    metersOf(rect.x),
    metersOf(rect.x + rect.sizeX),
    metersOf(rect.z),
    metersOf(rect.z + rect.sizeZ),
  )
  attachMesh(group, floor, createSurfaceMaterial(palette.floor, SURFACE_ROUGHNESS), 'floor', room.id)

  const walls = createSectionSet()
  const holes = collectFaceHoles(plan, room)

  for (const side of ROOM_SIDES) {
    const axis = SIDE_CONST_AXIS[side]
    const inward = SIDE_INWARD[side]
    const constValue = sideConstValue(room, side)
    const span = sideSpan(room, side)
    const solids = subtractHoles(span, height, holes[side])
    const exterior = isExteriorSide(plan, room, side)

    for (const solid of solids) {
      if (exterior) {
        const buffer = sectionOf(walls, 'wall', side)
        addVerticalQuad(buffer, axis, constValue, solid.u0, solid.u1, solid.v0, solid.v1, inward)
      } else {
        addSplitWallQuad(walls, side, constValue, solid, inward)
      }

      // 걸레받이는 바닥에 닿는 조각에만 붙는다. 문 아래는 조각 자체가 없어 자동으로 건너뛰고,
      // 창 아래는 창턱 밑 벽이 살아 있으므로 그대로 이어진다.
      // 벽면과 같은 `side`로 담아야 벽이 사라질 때 걸레받이 윗면이 흰 띠로 남지 않는다(결정 6).
      if (solid.v0 === 0) {
        addBaseboardRun(sectionOf(trim, 'baseboard', side), side, constValue, solid.u0, solid.u1)
      }
    }
  }

  attachSectionedMesh(
    group,
    walls,
    () => createSurfaceMaterial(palette.wall, SURFACE_ROUGHNESS),
    'wall',
    room.id,
  )
}

/** 문 개구부의 바닥 패치와 문틀. 개구부 칸은 어느 방의 rect에도 없으므로 바닥이 따로 필요하다. */
function buildDoorways(plan: FloorPlan, buffers: ShellBuffers): void {
  for (const doorway of plan.doorways) {
    const rect = openingRect(doorway.axis, doorway.cell, doorway.width)
    addFloorQuad(
      buffers.doorFloor,
      0,
      metersOf(rect.x),
      metersOf(rect.x + rect.sizeX),
      metersOf(rect.z),
      metersOf(rect.z + rect.sizeZ),
    )
    // 문은 항상 내벽 위에 있다(`Doorway`가 구획 둘을 잇는다는 정의상 그렇다).
    // 그래서 틀도 벽과 같은 높이에서 갈라 담는다 - 위쪽은 컷어웨이에서 벽과 함께 사라진다.
    // `side`는 `null`이다. 문틀은 두 구획이 공유하므로 어느 방향에서 봐도 남아야 한다.
    addOpeningFrame(
      sectionOf(buffers.trim, 'frame-lower', null),
      doorway.axis,
      doorway.cell,
      doorway.width,
      0,
      DOOR_HEIGHT_METERS,
      false,
      Number.NEGATIVE_INFINITY,
      INTERIOR_CUT_HEIGHT_METERS,
    )
    addOpeningFrame(
      sectionOf(buffers.trim, 'frame-upper', null),
      doorway.axis,
      doorway.cell,
      doorway.width,
      0,
      DOOR_HEIGHT_METERS,
      false,
      INTERIOR_CUT_HEIGHT_METERS,
      Number.POSITIVE_INFINITY,
    )
  }
}

/** 창틀 + 유리 + 창밖 색면. 유리와 색면은 실내를 향한 단면이라 궤도 뷰에서는 저절로 사라진다. */
function buildWindows(plan: FloorPlan, buffers: ShellBuffers): void {
  for (const windowOpening of plan.windows) {
    const room = requireRoom(plan, windowOpening.roomId)
    const side = requireOpeningSide(
      room,
      windowOpening.axis,
      windowOpening.cell,
      `창 '${windowOpening.id}'`,
    )

    const axis = windowOpening.axis
    const bandAxis = SIDE_CONST_AXIS[side]
    const inward = SIDE_INWARD[side]
    const band = bandRange(axis, windowOpening.cell)

    const start = axis === 'x' ? windowOpening.cell.x : windowOpening.cell.z
    const u0 = metersOf(start)
    const u1 = metersOf(start + windowOpening.width)
    const v0 = metersOf(windowOpening.sillCell)
    const v1 = metersOf(windowOpening.sillCell + windowOpening.heightCells)

    // 창은 보통 외벽에 있으므로 틀도 전체 높이로 남긴다. 다만 내벽 창을 만들 수도 있으므로
    // 그때는 문틀과 같은 규칙(허리에서 절단)을 따른다 - 안 그러면 잘린 벽 위에 창틀만 뜬다.
    if (isExteriorSide(plan, room, side)) {
      addOpeningFrame(
        sectionOf(buffers.trim, 'frame', side),
        axis,
        windowOpening.cell,
        windowOpening.width,
        v0,
        v1,
        true,
      )
    } else {
      const cell = windowOpening.cell
      const width = windowOpening.width
      const cut = INTERIOR_CUT_HEIGHT_METERS

      const lower = sectionOf(buffers.trim, 'frame-lower', side)
      addOpeningFrame(lower, axis, cell, width, v0, v1, true, Number.NEGATIVE_INFINITY, cut)

      const upper = sectionOf(buffers.trim, 'frame-upper', side)
      addOpeningFrame(upper, axis, cell, width, v0, v1, true, cut, Number.POSITIVE_INFINITY)
    }

    // 유리는 벽 두께 가운데. 실외면(inward의 반대쪽)에서 안쪽으로 파고든 위치다.
    const outerFace = inward === 1 ? band.near : band.far
    const innerFace = inward === 1 ? band.far : band.near
    const glassDepth = outerFace + (innerFace - outerFace) * GLASS_DEPTH_RATIO
    const inset = FRAME_THICKNESS_METERS

    addVerticalQuad(
      buffers.glass,
      bandAxis,
      glassDepth,
      u0 + inset,
      u1 - inset,
      v0 + inset,
      v1 - inset,
      inward,
    )

    const margin = OUTSIDE_PANEL_MARGIN_METERS
    addVerticalQuad(
      buffers.outside,
      bandAxis,
      outerFace,
      u0 - margin,
      u1 + margin,
      v0 - margin,
      v1 + margin,
      inward,
    )
  }
}

/** 벽이 깔고 앉은 칸의 바닥. 궤도 뷰에서 바닥에 구멍이 뚫린 것처럼 보이지 않게 메운다. */
function buildWallBaseFloor(plan: FloorPlan, buffer: SurfaceBuffer): void {
  const walkable = walkableFootprintCells(plan)

  for (let x = 0; x < plan.bounds.sizeX; x += 1) {
    for (let z = 0; z < plan.bounds.sizeZ; z += 1) {
      if (walkable.has(footprintKey(x, z))) continue
      addFloorQuad(buffer, 0, metersOf(x), metersOf(x + 1), metersOf(z), metersOf(z + 1))
    }
  }
}

/** 외벽 링에 속한 칸인가. 링 위의 벽은 자르지 않으므로 마루도 덮지 않는다. */
function isOuterRingCell(plan: FloorPlan, x: number, z: number): boolean {
  return (
    x < OUTER_WALL_CELLS ||
    z < OUTER_WALL_CELLS ||
    x >= plan.bounds.sizeX - OUTER_WALL_CELLS ||
    z >= plan.bounds.sizeZ - OUTER_WALL_CELLS
  )
}

/**
 * 잘린 내벽의 마루(단면 뚜껑). 절단 높이에 벽 두께만큼 덮는 위쪽 면이다.
 *
 * 방마다 자기 안쪽 면만 그리므로(결정 2) 잘린 벽은 두께 0인 판 두 장이 된다.
 * 마루가 없으면 부감에서 그게 종잇장으로 읽힌다 - 걸레받이·문틀과 같은 이유의 필수 디테일이다.
 *
 * **바닥 셀에서 유도**하는 것이 중요하다. 방의 벽면마다 만들면 한 벽에 마루가 두 장 겹쳐
 * z-fighting이 난다. 통행 가능 칸을 빼면 문 개구부에는 자동으로 마루가 생기지 않는다 -
 * 문이 허리 높이 벽의 잘린 틈으로 읽히는 것이 정확히 원하는 그림이다.
 */
function buildInteriorWallTops(plan: FloorPlan, buffer: SurfaceBuffer): void {
  const walkable = walkableFootprintCells(plan)

  for (let x = 0; x < plan.bounds.sizeX; x += 1) {
    for (let z = 0; z < plan.bounds.sizeZ; z += 1) {
      if (walkable.has(footprintKey(x, z))) continue
      if (isOuterRingCell(plan, x, z)) continue

      addFloorQuad(
        buffer,
        INTERIOR_CUT_HEIGHT_METERS,
        metersOf(x),
        metersOf(x + 1),
        metersOf(z),
        metersOf(z + 1),
      )
    }
  }
}

/**
 * 평면도(또는 단순 `RoomBounds`)를 밀폐 실내 지오메트리로 굽고 씬에 붙인다.
 *
 * `RoomBounds`를 받는 경로가 있는 이유: 평면도를 모르는 호출부도 밀폐된 방 하나를 얻을 수 있어야
 * 셸이 특정 평면도에 묶이지 않는다. 그 경우 `createSingleRoomPlan`이 어댑터 역할을 한다.
 */
export function buildRoomShell(
  ctx: SceneCtx,
  source: FloorPlan | RoomBounds,
  options: RoomShellOptions = {},
): RoomShell {
  const plan = 'rooms' in source ? source : createSingleRoomPlan(source)
  assertValidFloorPlan(plan)

  // 크기 검증·미터 변환은 scene.ts가 담당한다. 두 벌이 되면 셸과 카메라의 기준이 갈린다.
  const { width, height, depth } = roomWorldSize(plan.bounds)

  const group = new THREE.Group()
  group.name = ROOM_SHELL_GROUP_NAME

  const buffers: ShellBuffers = {
    wallBase: createBuffer(),
    doorFloor: createBuffer(),
    trim: createSectionSet(),
    glass: createBuffer(),
    outside: createBuffer(),
  }

  for (const room of plan.rooms) buildRoom(group, plan, room, height, buffers.trim)
  buildDoorways(plan, buffers)
  buildWindows(plan, buffers)
  buildWallBaseFloor(plan, buffers.wallBase)
  buildInteriorWallTops(plan, sectionOf(buffers.trim, 'wall-top', null))

  attachMesh(group, buffers.wallBase, createSurfaceMaterial(WALL_BASE_COLOR, SURFACE_ROUGHNESS), 'floor')
  attachMesh(group, buffers.doorFloor, createSurfaceMaterial(ROOM_PALETTE.hall.floor, SURFACE_ROUGHNESS), 'floor')
  attachSectionedMesh(group, buffers.trim, () => createSurfaceMaterial(TRIM_COLOR, TRIM_ROUGHNESS), 'trim')

  const glassMaterial = createSurfaceMaterial(GLASS_COLOR, GLASS_ROUGHNESS)
  glassMaterial.transparent = true
  glassMaterial.opacity = GLASS_OPACITY
  // 유리끼리 겹쳐 그려질 일이 없고, 깊이 기록을 끄면 뒤 소품이 유리에 가려 사라지는 사고가 없다.
  glassMaterial.depthWrite = false
  attachMesh(group, buffers.glass, glassMaterial, 'glass')

  // 창밖은 광원이 아니라 "바깥이 있다"는 기호다. 조명에 반응하면 방마다 밝기가 달라져 기호성이 깨진다.
  const outsideMaterial = new THREE.MeshBasicMaterial({ color: OUTSIDE_COLOR, side: THREE.FrontSide })
  attachMesh(group, buffers.outside, outsideMaterial, 'outside')

  const ceilingBuffer = createBuffer()
  addCeilingQuad(ceilingBuffer, height, 0, width, 0, depth)
  const ceiling = attachMesh(
    group,
    ceilingBuffer,
    createSurfaceMaterial(CEILING_COLOR, SURFACE_ROUGHNESS),
    'ceiling',
  )
  if (ceiling === null) throw new Error('천장 지오메트리를 만들지 못했다 - 방 크기가 0이다')
  ceiling.name = SHELL_CEILING_NAME

  // 컷어웨이가 셸과 다른 평면도로 만들어지면 엉뚱한 면을 자르게 된다. 그 사고를 즉시 터뜨리려고 심는다.
  group.userData[SHELL_PLAN_ID_KEY] = plan.id

  const shell: RoomShell = { group, plan, ceiling }
  setCutawayMode(shell, options.cutaway ?? true)

  ctx.scene.add(group)

  return shell
}

/**
 * 컷어웨이 모드 전환 - **모드만** 바꾼다. 카메라 방향은 `render/cutaway.ts`가 따로 본다.
 *
 * 여기서 손대는 것은 두 가지다.
 *  1. **천장** - 부감에서 천장이 남아 있으면 실내가 통째로 가려진다. 반대로 1인칭에서 지우면
 *     밀폐감이 사라지고 위를 볼 때 배경색이 그대로 보인다.
 *  2. **내벽 허리 위(`wall-upper`·`frame-upper`)와 벽마루(`wall-top`)** - 결정 5의 절단 그 자체다.
 *     1인칭에서는 반대로 허리 위를 되살리고 마루를 감춰야 원래의 밀폐된 방이 된다.
 *
 * 방향별 섹션(`side !== null`)은 **일부러 건드리지 않는다.** 두 주체가 같은 머티리얼을 쓰면
 * 모드 전환 한 번에 방향 상태가 조용히 날아간다. 1인칭으로 돌아갈 때 방향 숨김을 되돌리는 것은
 * `Cutaway.dispose()`의 책임이다.
 */
export function setCutawayMode(shell: RoomShell, enabled: boolean): void {
  shell.ceiling.visible = !enabled
  shell.group.userData['cutaway'] = enabled

  forEachShellSection(shell.group, (material, meta) => {
    if (!isModeOwnedRole(meta.role)) return
    material.visible = isSectionVisibleForMode(meta.role, enabled)
  })
}

export function isCutawayEnabled(shell: RoomShell): boolean {
  return shell.group.userData['cutaway'] === true
}

/**
 * 셸을 씬에서 떼고 GPU 리소스를 해제한다.
 *
 * 라운드마다 평면도가 바뀔 수 있고(컨셉시트 4.1의 템플릿 셸), Three.js는 GPU 자원을 GC 하지 않는다.
 * `scene.remove()`만 하면 지오메트리·머티리얼이 그대로 남아 세션이 길어질수록 확실히 터진다.
 */
export function disposeRoomShell(shell: RoomShell): void {
  shell.group.removeFromParent()

  shell.group.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return

    node.geometry.dispose()

    // 이 파일은 메시마다 머티리얼을 새로 만들지만, 배열 머티리얼이 섞여도 새지 않게 둘 다 처리한다.
    const materials = Array.isArray(node.material) ? node.material : [node.material]
    for (const material of materials) material.dispose()
  })

  shell.group.clear()
}
